import { useState, useCallback, useEffect, useRef } from "react";
import {
    CASE_TYPE_MAP,
    CREATE_REQUEST_STATUS,
    DISCOVERY_PREFERENCE_TYPES,
    DISCOVERY_TYPE_MAP,
} from "@demandproai/shared";
import useAxios from "./useAxios";
import useSocket from "./useSocket";
import Constants from "../Constants";
import { toaster } from "../utils/toast";

const CASE_TYPE_OPTIONS = Object.values(CASE_TYPE_MAP).map(({ value, text }) => ({ value, text }));

const DISCOVERY_TYPE_OPTIONS = DISCOVERY_PREFERENCE_TYPES.map((value) => ({
    value,
    text: DISCOVERY_TYPE_MAP[value].text,
}));

// owns only the preference selection and its file-extraction state, the question list is self-contained
const useDiscoveryPreference = () => {
    const { postData, isLoading: isPreferenceLoading } = useAxios();

    const [selectedCaseType, setCaseType] = useState("");
    const [selectedDiscoveryType, setDiscoveryType] = useState("");
    const [preferenceId, setPreferenceId] = useState(null);
    const [extractingFileCount, setExtractingFileCount] = useState(0);
    const [questionsRefreshToken, setQuestionsRefreshToken] = useState(0);
    const resolveRequestRef = useRef(0);

    const hasSelection = Boolean(selectedCaseType && selectedDiscoveryType);

    const setSelectedCaseType = useCallback((caseType) => {
        setPreferenceId(null);
        setCaseType(caseType);
    }, []);

    const setSelectedDiscoveryType = useCallback((discoveryType) => {
        setPreferenceId(null);
        setDiscoveryType(discoveryType);
    }, []);

    const resolvePreferenceId = useCallback(async () => {
        if (!hasSelection) {
            setPreferenceId(null);
            return;
        }

        const requestId = resolveRequestRef.current + 1;
        resolveRequestRef.current = requestId;

        const resp = await postData(Constants.ApiUrl.discoveryPreference.getPreferenceId, {
            caseType: selectedCaseType,
            discoveryType: selectedDiscoveryType,
        });

        if (requestId !== resolveRequestRef.current) return;

        if (!resp?.success) {
            toaster({ message: resp?.message || "Failed to load discovery preference", success: false });
            setPreferenceId(null);
            return;
        }
        setPreferenceId(resp.data?.preferenceId ?? null);
    }, [hasSelection, selectedCaseType, selectedDiscoveryType, postData]);

    useEffect(() => {
        resolvePreferenceId();
    }, [resolvePreferenceId]);

    useEffect(() => {
        setExtractingFileCount(0);
    }, [preferenceId]);

    const markFilesExtracting = useCallback(({ fileCount }) => {
        if (!fileCount) return;
        setExtractingFileCount((prev) => prev + fileCount);
    }, []);

    const handleRecordStatusUpdate = useCallback((payload) => {
        if (!preferenceId || String(payload?.preferenceId) !== String(preferenceId)) return;

        const isExtractionSettled = [CREATE_REQUEST_STATUS.COMPLETED, CREATE_REQUEST_STATUS.FAILED, CREATE_REQUEST_STATUS.BAD_RECORD].includes(payload.status);
        if (!isExtractionSettled) return;
        setExtractingFileCount((prev) => Math.max(prev - 1, 0));

        if (payload.status !== CREATE_REQUEST_STATUS.COMPLETED) return;
        // bumping the token is all the list needs to refetch itself
        setQuestionsRefreshToken((prev) => prev + 1);
    }, [preferenceId]);

    useSocket({
        event: "caseRecordStatusUpdate",
        handler: handleRecordStatusUpdate,
        enabled: !!preferenceId
    });

    return {
        caseTypeOptions: CASE_TYPE_OPTIONS,
        discoveryTypeOptions: DISCOVERY_TYPE_OPTIONS,
        selectedCaseType,
        setSelectedCaseType,
        selectedDiscoveryType,
        setSelectedDiscoveryType,
        preferenceId,
        hasSelection,
        isPreferenceLoading,
        isExtractingQuestions: extractingFileCount > 0,
        markFilesExtracting,
        questionsRefreshToken,
    };
};

export default useDiscoveryPreference;
